import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Chip } from "@/components/Chip";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { useApi } from "@/lib/api";
import { formatDate } from "@/lib/formatDate";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { plural } from "@/lib/plural";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { MemberStack } from "./MemberStack";
import type { Team } from "./shared";

const COLUMNS = ["Team", "Members", "Created by", "Created"] as const;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <tr key={index} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3.5 w-40 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-56 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-6 w-20 rounded-full bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 shrink-0 rounded-full bg-surface" />
              <div className="h-3 w-28 rounded bg-surface" />
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-20 rounded bg-surface" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function MemberTeams() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [error, setError] = useState<string | null>(null);

  const hasFilters = Boolean(debouncedNameFilter);

  const fetchTeams = useCallback(async () => {
    if (!accessToken || !orgId) {
      return null;
    }

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });

    if (debouncedNameFilter) {
      params.set("name", debouncedNameFilter);
    }

    return api.get<PagedResult<Team>>(
      `/organizations/${orgId}/teams?${params.toString()}`,
    );
  }, [accessToken, api, orgId, page, debouncedNameFilter]);

  const applyTeams = useCallback((data: PagedResult<Team> | null) => {
    if (data) {
      setTeams(data.items);
      setTotalCount(data.totalCount);
      setError(null);
    } else {
      setError("Couldn't load teams.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchTeams().then((data) => {
      if (!cancelled) {
        applyTeams(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchTeams, applyTeams]);

  function retry() {
    setError(null);
    setTeams(null);
    void fetchTeams().then(applyTeams);
  }

  function clearFilters() {
    setNameFilter("");
    setPage(1);
  }

  const isLoading = teams === null && !error;
  const isEmpty = teams !== null && totalCount === 0;

  return (
    <div className="text-foreground">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Teams</h1>
        {teams !== null && (
          <p className="mt-0.5 text-sm text-foreground/70">
            {plural(totalCount, "team")}
          </p>
        )}
      </div>

      <div className="mb-4 flex gap-2">
        <SearchField
          value={nameFilter}
          onChange={(value) => {
            setNameFilter(value);
            setPage(1);
          }}
          label="Search teams by name"
          placeholder="Search team name"
        />
      </div>

      {hasFilters && teams !== null && (
        <div className="mb-4 flex items-center gap-3 text-sm text-foreground/70">
          <span>Filtered to {plural(totalCount, "team")}</span>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent/20"
          >
            Clear filters
          </button>
        </div>
      )}

      {error && <LoadError what="teams" onRetry={retry} className="mb-4" />}

      {isEmpty ? (
        <EmptyState
          title={hasFilters ? "No teams match these filters" : "No teams yet"}
          description={
            hasFilters
              ? "Try a different name."
              : "Your organization has no teams yet."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows />
              ) : (
                teams?.map((team) => (
                  <tr
                    key={team.id}
                    onClick={() =>
                      navigate(`/${orgId}/teams/${team.id}/members`)
                    }
                    className="group h-14 cursor-pointer border-b border-surface/70 hover:bg-surface"
                  >
                    <td className="py-2 pr-4">
                      <div className="text-foreground">{team.name}</div>
                      {team.description && (
                        <div className="truncate text-xs text-foreground/70">
                          {team.description}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <MemberStack members={team.members} />
                    </td>
                    <td className="py-2 pr-4">
                      {team.createdBy ? (
                        <div className="flex items-center gap-2">
                          <Avatar
                            name={team.createdBy.name}
                            email={team.createdBy.email}
                            avatarId={team.createdBy.avatarId}
                            size="md"
                          />
                          <span className="text-foreground/70">
                            {team.createdBy.name}
                          </span>
                        </div>
                      ) : (
                        <Chip>Created by SCIM</Chip>
                      )}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDate(team.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        page={page}
        totalPages={Math.ceil(totalCount / PAGE_SIZE)}
        onPageChange={setPage}
      />
    </div>
  );
}
